import React, { useState, useEffect, useCallback } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { VeniceBalanceLine } from '../components/VeniceBalance';
import { 
  ArrowLeft, Settings2, Check, AlertTriangle, Key, HardDrive, 
  FolderOpen, Cloud, Server, Trash2, Loader2, Twitter, LogOut,
  ChevronRight, Shield, RefreshCw, Monitor, Smartphone, Tablet, X, Crown, Plug
} from 'lucide-react';
import { UserProfile, ApiKeys, DeviceSession, SESSION_LIMIT } from '../types';
import { 
  isTwitterConnected, 
  getTwitterUsername, 
  getTwitterUserTier, 
  disconnectTwitter 
} from '../utils/twitter';
import { isFileSystemAccessSupported, pickSaveDirectory, setAutoSaveEnabled, syncLocalStoragePrefsToCloud, listLocalWorkflows, getWorkflowsDirectory, CustomWorkflow } from '../utils/localSave';
import { ProfileMenu } from '../components/ProfileMenu';
import { AdminPanel } from '../components/AdminPanel';
import { ClaudeConnectorPanel } from '../components/ClaudeConnectorPanel';
import { getComfyUIClient, getComfyUIConfig, saveComfyUIConfig, ComfyUIConfig } from '../utils/comfyui';
import { useAppVersion } from '../hooks/useAppVersion';
import { desktopApp } from '../utils/desktopFolder';
import { formatRelativeTime } from '../utils/workspaceLibrary';
import { setVeniceSafeMode, veniceSafeModeOn } from '../utils/veniceSafeMode';
import { SettingsNav, type SettingsNavId } from '../components/settings/SettingsNav';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { useTheme } from '../hooks/useTheme';
import { Moon, Sun } from 'lucide-react';
import type { ThemeSetting } from '../utils/theme';

interface SettingsPageProps {
  user: UserProfile | null;
  onBack: () => void;
  isFirebaseConfigured: boolean;
  apiKeys: ApiKeys;
  inputApiKeys: {
    googleGenAi: string;
    openAi: string;
    venice: string;
    vertexApiKey: string;
    vertexProjectId: string;
    vertexLocation: string;
  };
  onInputApiKeysChange: (keys: any) => void;
  onSaveApiKeys: () => void;
  /** Forget one saved key; Save never clears keys on its own. */
  onRemoveApiKey?: (name: keyof ApiKeys) => void;
  /** Saved keys are kept after the app closes (the desktop app), rather than for this tab only */
  keysLast?: boolean;
  /** Tab to open on; a node asking for a key lands on the API keys tab. */
  initialTab?: SettingsTab;
  autoSaveEnabled: boolean;
  autoSaveFolderName: string | null;
  /** The folder inside it that this account's files go under (utils/localPlaces.ts accountFolderName); none signed out */
  autoSaveAccountFolder?: string | null;
  onAutoSaveEnabledChange: (enabled: boolean) => void;
  onSelectLocalFolder: () => void;
  onShowTwitterAuth: () => void;
  // Account deletion
  onDeleteAccount?: () => void;
  /** Whoever runs the site sees the Admin tab (utils/proGrant.ts useIsAdmin) */
  isAdmin?: boolean;
  // Navigation
  isPro?: boolean;
  onSubscription?: () => void;
  onAnalytics?: () => void;
  onSignOut?: () => void;
  // ComfyUI
  comfyUIConfig: ComfyUIConfig;
  onComfyUIConfigChange: (config: ComfyUIConfig) => void;
  customWorkflows: CustomWorkflow[];
  onRefreshWorkflows: () => void;
  customWorkflowsLoading: boolean;
  customWorkflowInfo: string | null;
  customWorkflowError: string | null;
  onEnsureWorkflowsFolder: () => void;
  // Session management
  activeSessions?: DeviceSession[];
  currentSessionId?: string;
  onRevokeSession?: (sessionId: string) => void;
  onRevokeAllOtherSessions?: () => void;
}

/** The sections: account (the profile), devices, the keys, X, the local folder (autosave), Claude, appearance, local models (comfyui), status, admin */
export type SettingsTab = 'status' | 'api' | 'twitter' | 'autosave' | 'comfyui' | 'claude' | 'account' | 'devices' | 'appearance' | 'admin';

/** The three ways the app can look, as the Appearance tab offers them */
const THEME_CHOICES: { value: ThemeSetting; label: string; help: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', help: 'Cream ground, white cards', icon: <Sun size={14} /> },
  { value: 'dark', label: 'Dark', help: 'Charcoal ground, lighter tints', icon: <Moon size={14} /> },
  { value: 'system', label: 'Match the system', help: 'Follows the computer, day and night', icon: <Monitor size={14} /> },
];

interface KeyFieldProps {
  label: string;
  name: keyof ApiKeys;
  /** The value currently stored; when set, the field shows as saved until Replace is chosen. */
  savedValue?: string;
  inputValue: string;
  editing: boolean;
  /** Secrets are shown as a mask; ids and regions are shown in clear. */
  secret?: boolean;
  placeholder: string;
  hint?: React.ReactNode;
  onChange: (value: string) => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onRemove?: () => void;
}

/**
 * One API key. A saved key is displayed read-only with Replace and Remove; the
 * input only appears when there is nothing saved or Replace was chosen, and it is
 * never bound to the mask, so what is typed is exactly what gets saved.
 */
const KeyField: React.FC<KeyFieldProps> = ({
  label, name, savedValue, inputValue, editing, secret = true, placeholder, hint, onChange, onEdit, onCancelEdit, onRemove,
}) => {
  const saved = !!savedValue;
  // A pending value is always visible: Save writes whatever is typed, even after the page was left and reopened.
  const showInput = !saved || editing || !!inputValue;
  const inputId = `api-key-${name}`;
  return (
    <div>
      <label htmlFor={showInput ? inputId : undefined} className="ns-field-label">{label}</label>
      {showInput ? (
        <div className="flex flex-wrap gap-2">
          <input
            id={inputId}
            type={secret ? 'password' : 'text'}
            autoComplete="off"
            autoFocus={editing}
            className="ns-field flex-1 min-w-28"
            value={inputValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={saved ? `Paste the new ${label}` : placeholder}
          />
          {saved && (
            <button type="button" onClick={onCancelEdit} className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat shrink-0">
              Keep current
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {/* A saved key reads as a field that cannot be typed into: the frame of ns-field, on the quiet surface */}
          <div className="ns-t-mono flex-1 min-w-28 px-3 py-2 rounded-md border border-cream-300 bg-cream-50 leading-5 text-ink-soft truncate" aria-label={`${label} saved`}>
            {secret ? '••••••••••••••••' : savedValue}
          </div>
          <button type="button" onClick={onEdit} className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat shrink-0">
            Replace
          </button>
          {onRemove && (
            <button type="button" onClick={onRemove} className="ns-btn ns-btn-sm ns-btn-danger ns-btn-flat shrink-0">
              Remove
            </button>
          )}
        </div>
      )}
      {hint && <p className="ns-t-small mt-1.5">{hint}</p>}
    </div>
  );
};

export const SettingsPage: React.FC<SettingsPageProps> = ({
  user,
  onBack,
  isFirebaseConfigured,
  apiKeys,
  inputApiKeys,
  onInputApiKeysChange,
  onSaveApiKeys,
  onRemoveApiKey,
  keysLast = false,
  initialTab,
  autoSaveEnabled,
  autoSaveFolderName,
  autoSaveAccountFolder = null,
  onAutoSaveEnabledChange,
  onSelectLocalFolder,
  onShowTwitterAuth,
  onDeleteAccount,
  isAdmin = false,
  isPro,
  onSubscription,
  onAnalytics,
  onSignOut,
  comfyUIConfig,
  onComfyUIConfigChange,
  customWorkflows,
  onRefreshWorkflows,
  customWorkflowsLoading,
  customWorkflowInfo,
  customWorkflowError,
  onEnsureWorkflowsFolder,
  activeSessions,
  currentSessionId,
  onRevokeSession,
  onRevokeAllOtherSessions,
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab ?? 'status');
  const theme = useTheme();
  const savedKeyCount = [apiKeys.googleGenAi, apiKeys.openAi, apiKeys.venice, apiKeys.vertexApiKey].filter(Boolean).length;
  // Saved keys are shown masked; a field only becomes editable through Replace, and a blank
  // field on Save keeps its saved value.
  const [editingKeys, setEditingKeys] = useState<Partial<Record<keyof ApiKeys, boolean>>>({});
  // Venice's safe mode (utils/veniceSafeMode.ts): saved at once, on this device
  const [veniceSafeMode, setVeniceSafeModeState] = useState(veniceSafeModeOn);
  const startEditingKey = (name: keyof ApiKeys) => setEditingKeys(prev => ({ ...prev, [name]: true }));
  const stopEditingKey = (name: keyof ApiKeys) => {
    setEditingKeys(prev => ({ ...prev, [name]: false }));
    onInputApiKeysChange({ ...inputApiKeys, [name]: '' });
  };
  const handleSaveKeys = () => {
    onSaveApiKeys();
    setEditingKeys({});
  };
  const autoSaveSupported = isFileSystemAccessSupported();
  const appVersion = useAppVersion();
  // The desktop app's own version (its installer's number), beside the web build's
  const desktopVersion = desktopApp()?.version || null;
  const inRouter = useInRouterContext();
  
  // Twitter state
  const [twitterConnected, setTwitterConnected] = useState(false);
  const [twitterUsername, setTwitterUsername] = useState<string | null>(null);
  const [twitterUserTier, setTwitterUserTier] = useState<'basic' | 'premium'>('basic');

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Session management state
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  // ComfyUI state
  const [comfyUIStatus, setComfyUIStatus] = useState<'idle' | 'testing' | 'connected' | 'error'>('idle');
  const [comfyUIError, setComfyUIError] = useState<string | null>(null);
  const [localComfyConfig, setLocalComfyConfig] = useState(comfyUIConfig);

  useEffect(() => {
    // Check Twitter connection on mount
    setTwitterConnected(isTwitterConnected());
    setTwitterUsername(getTwitterUsername());
    setTwitterUserTier(getTwitterUserTier());
  }, []);

  useEffect(() => {
    setLocalComfyConfig(comfyUIConfig);
  }, [comfyUIConfig]);

  const handleDisconnectTwitter = () => {
    disconnectTwitter();
    setTwitterConnected(false);
    setTwitterUsername(null);
    setTwitterUserTier('basic');
  };

  const handleTestComfyUI = async () => {
    setComfyUIStatus('testing');
    setComfyUIError(null);
    
    try {
      const client = getComfyUIClient();
      if (!client) {
        throw new Error('ComfyUI not configured');
      }
      
      // Simple connection test - just try to reach the server
      const response = await fetch(`${localComfyConfig.baseUrl}/system_stats`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      
      if (response.ok) {
        setComfyUIStatus('connected');
      } else {
        throw new Error('ComfyUI server not responding');
      }
    } catch (err: any) {
      setComfyUIStatus('error');
      setComfyUIError(err.message || 'Connection failed');
    }
  };

  const handleSaveComfyConfig = () => {
    saveComfyUIConfig(localComfyConfig);
    onComfyUIConfigChange(localComfyConfig);
  };

  // The plan lives on its own page, beside this one in the same navigation
  const selectSection = (id: SettingsNavId) => {
    if (id === 'plan') { onSubscription?.(); return; }
    setActiveTab(id);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-cream overflow-hidden">
      {/* A column as tall as the window: the bar and the footer stay, the content scrolls between them */}
      <div className="h-full flex flex-col">
        {/* The bar every page has: the mark, the way back, where you are; the account on the right */}
        <header className="ns-pagebar px-4">
          <div className="w-full flex items-center gap-3">
            <span className="flex items-center gap-2.5 shrink-0" aria-hidden="true">
              <BrandMark size={32} />
              <span className="text-[1.0625rem] font-bold tracking-[-0.02em] hidden md:inline">Noodle Studio</span>
              <BetaTag className="hidden md:inline-flex" />
            </span>
            <button
              onClick={onBack}
              className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat group rounded-full"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>Back to canvas</span>
            </button>
            <span className="ns-t-small hidden sm:flex items-center gap-1.5"><ChevronRight size={12} aria-hidden="true" />Settings</span>
            <span className="flex-1" />

            <ProfileMenu
              user={user}
              isPro={isPro}
              onSubscription={onSubscription}
              onAnalytics={onAnalytics}
              onSignOut={onSignOut}
            />
          </div>
        </header>

        {/* Main content: in the gutter and the measure of the bar */}
        <main className="ns-scroll-fade-top flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Page title */}
            <div className="flex items-center gap-3 mb-6">
              <span className="ns-mark ns-mark-lavender" aria-hidden="true">
                <Settings2 size={20} />
              </span>
              <div className="min-w-0">
                <h1 className="ns-t-page">Settings</h1>
                <p className="text-sm text-ink-soft">Your account, your keys, what the app connects to and how it behaves</p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* The sections, in groups */}
              <div className="lg:w-60 shrink-0">
                <SettingsNav
                  active={activeTab}
                  onSelect={selectSection}
                  signedIn={!!user}
                  isAdmin={isAdmin}
                  isPro={isPro}
                  twitterConnected={twitterConnected}
                  keyCount={savedKeyCount}
                />
              </div>

              {/* Content Area */}
              <div className="flex-1 min-w-0">
                {/* Appearance: light, dark, or the system's */}
                {activeTab === 'appearance' && (
                  <div className="space-y-4">
                    <section className="ns-card">
                      <h2 className="text-lg font-semibold text-ink mb-1">Appearance</h2>
                      <p className="text-sm text-ink-soft mb-4">The whole app follows: the canvas, the library, the pages and the panels. Kept on this device.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Theme">
                        {THEME_CHOICES.map(choice => (
                          <button
                            key={choice.value}
                            type="button"
                            role="radio"
                            aria-checked={theme.setting === choice.value}
                            onClick={() => theme.setSetting(choice.value)}
                            className="ns-lib-option flex-col gap-2.5 p-2.5"
                            data-theme-choice={choice.value}
                          >
                            <span className={`ns-theme-swatch ns-theme-swatch-${choice.value}`} aria-hidden="true" />
                            <span className="flex items-center gap-2 text-sm font-semibold">
                              {choice.icon}{choice.label}
                              {theme.setting === choice.value && <Check size={14} className="ml-auto text-lavender-deep" aria-hidden="true" />}
                            </span>
                            <span className="ns-t-small">{choice.help}</span>
                          </button>
                        ))}
                      </div>
                      <p className="ns-t-meta mt-4">Showing {theme.theme === 'dark' ? 'dark' : 'light'} now{theme.setting === 'system' ? ', as the system is' : ''}.</p>
                    </section>
                  </div>
                )}

                {/* Status Tab */}
                {activeTab === 'status' && (
                  <div className="space-y-4">
                    <section className="ns-card">
                      <h2 className="text-lg font-semibold text-ink mb-4">Backend status</h2>

                      {isFirebaseConfigured ? (
                        <div className="ns-note flex-col items-stretch gap-1 p-4" data-tone="ok">
                          <h3 className="text-sm font-semibold flex items-center gap-2">
                            <Check size={16} /> Cloud backend active
                          </h3>
                          <p>Firebase is successfully configured. Cloud sync is available.</p>
                        </div>
                      ) : (
                        <div className="ns-note flex-col items-stretch gap-1 p-4" data-tone="warn">
                          <h3 className="text-sm font-semibold flex items-center gap-2">
                            <AlertTriangle size={16} /> Cloud backend inactive
                          </h3>
                          <p>Running in local mode only. Sign in to enable cloud features.</p>
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {/* API Keys Tab */}
                {activeTab === 'api' && (
                  <div className="space-y-4">
                    <section className="ns-card">
                      <h2 className="text-lg font-semibold text-ink mb-1">API keys</h2>
                      <p className="text-sm text-ink-soft mb-1">Keys from Venice, Google or OpenAI. They stay on this computer, encrypted for your account; every ask shows its price before you press it, and the cost stays with you.</p>
                      <p className="ns-t-meta mb-6" data-keys-kept={keysLast ? 'computer' : 'session'}>
                        {keysLast
                          ? 'Saved keys stay on this computer, encrypted by Windows for your account, and are there each time the app opens.'
                          : 'Saved keys stay in this tab only and are gone when it closes. The Windows app keeps them between visits.'}
                      </p>

                      <div className="space-y-5">
                        <KeyField
                          label="Google Gemini API key"
                          name="googleGenAi"
                          savedValue={apiKeys.googleGenAi}
                          inputValue={inputApiKeys.googleGenAi}
                          editing={!!editingKeys.googleGenAi}
                          placeholder="Enter your Google Gemini API key"
                          onChange={(value) => onInputApiKeysChange({ ...inputApiKeys, googleGenAi: value })}
                          onEdit={() => startEditingKey('googleGenAi')}
                          onCancelEdit={() => stopEditingKey('googleGenAi')}
                          onRemove={onRemoveApiKey ? () => onRemoveApiKey('googleGenAi') : undefined}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <KeyField
                            label="Vertex API key"
                            name="vertexApiKey"
                            savedValue={apiKeys.vertexApiKey}
                            inputValue={inputApiKeys.vertexApiKey}
                            editing={!!editingKeys.vertexApiKey}
                            placeholder="Vertex API key"
                            onChange={(value) => onInputApiKeysChange({ ...inputApiKeys, vertexApiKey: value })}
                            onEdit={() => startEditingKey('vertexApiKey')}
                            onCancelEdit={() => stopEditingKey('vertexApiKey')}
                            onRemove={onRemoveApiKey ? () => onRemoveApiKey('vertexApiKey') : undefined}
                          />
                          <KeyField
                            label="Vertex project ID"
                            name="vertexProjectId"
                            secret={false}
                            savedValue={apiKeys.vertexProjectId}
                            inputValue={inputApiKeys.vertexProjectId}
                            editing={!!editingKeys.vertexProjectId}
                            placeholder="my-gcp-project"
                            onChange={(value) => onInputApiKeysChange({ ...inputApiKeys, vertexProjectId: value })}
                            onEdit={() => startEditingKey('vertexProjectId')}
                            onCancelEdit={() => stopEditingKey('vertexProjectId')}
                            onRemove={onRemoveApiKey ? () => onRemoveApiKey('vertexProjectId') : undefined}
                          />
                          <KeyField
                            label="Vertex location"
                            name="vertexLocation"
                            secret={false}
                            savedValue={apiKeys.vertexLocation}
                            inputValue={inputApiKeys.vertexLocation}
                            editing={!!editingKeys.vertexLocation}
                            placeholder="us-central1"
                            onChange={(value) => onInputApiKeysChange({ ...inputApiKeys, vertexLocation: value })}
                            onEdit={() => startEditingKey('vertexLocation')}
                            onCancelEdit={() => stopEditingKey('vertexLocation')}
                          />
                        </div>

                        <KeyField
                          label="OpenAI API key"
                          name="openAi"
                          savedValue={apiKeys.openAi}
                          inputValue={inputApiKeys.openAi}
                          editing={!!editingKeys.openAi}
                          placeholder="Enter your OpenAI API key"
                          onChange={(value) => onInputApiKeysChange({ ...inputApiKeys, openAi: value })}
                          onEdit={() => startEditingKey('openAi')}
                          onCancelEdit={() => stopEditingKey('openAi')}
                          onRemove={onRemoveApiKey ? () => onRemoveApiKey('openAi') : undefined}
                        />

                        <KeyField
                          label="Venice AI API key"
                          name="venice"
                          savedValue={apiKeys.venice}
                          inputValue={inputApiKeys.venice}
                          editing={!!editingKeys.venice}
                          placeholder="Enter your Venice AI API key"
                          hint={<>
                            Unlocks Seedance, Kling, Nano Banana Pro and GPT Image models. Create a key at venice.ai → Settings → API.
                            {apiKeys.venice && <VeniceBalanceLine apiKey={apiKeys.venice} />}
                          </>}
                          onChange={(value) => onInputApiKeysChange({ ...inputApiKeys, venice: value })}
                          onEdit={() => startEditingKey('venice')}
                          onCancelEdit={() => stopEditingKey('venice')}
                          onRemove={onRemoveApiKey ? () => onRemoveApiKey('venice') : undefined}
                        />
                        <label className="ns-well flex items-center justify-between gap-3 p-3 cursor-pointer">
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-ink">Blur adult content in Venice pictures</span>
                            <span className="block ns-t-meta">Venice's safe mode, on by default. Turned off, pictures it marks as adult come back unblurred. Kept on this device; it applies to pictures made from then on.</span>
                          </span>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={veniceSafeMode}
                            onChange={(e) => { setVeniceSafeMode(e.target.checked); setVeniceSafeModeState(e.target.checked); }}
                            className="ns-switch"
                            aria-label="Blur adult content in Venice pictures"
                          />
                        </label>
                      </div>

                      <div className="flex justify-end mt-6">
                        <button onClick={handleSaveKeys} className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat">
                          Save keys
                        </button>
                      </div>
                    </section>
                  </div>
                )}

                {/* Twitter Tab */}
                {activeTab === 'twitter' && (
                  <div className="space-y-4">
                    <section className="ns-card">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="ns-mark ns-mark-lavender" aria-hidden="true">
                          <Twitter size={20} />
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-lg font-semibold text-ink">X (Twitter)</h2>
                          <p className="text-sm text-ink-soft">Post pictures and videos from a Post node on the canvas, with a caption</p>
                        </div>
                      </div>

                      {twitterConnected ? (
                        <div className="space-y-4">
                          <div className="ns-well flex items-center justify-between gap-3 p-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="ns-mark ns-mark-sm ns-mark-lavender" aria-hidden="true">
                                <Twitter size={16} />
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-ink truncate">@{twitterUsername}</p>
                                <p className="text-sm text-ink-soft flex items-center gap-2">
                                  <span className="ns-tone-dot" data-tone="ok" />
                                  Connected
                                  {twitterUserTier === 'premium' && (
                                    <span className="ns-chip ns-chip-sm ns-chip-lavender ml-1">
                                      Premium
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={handleDisconnectTwitter}
                              className="ns-btn ns-btn-sm ns-btn-danger ns-btn-flat shrink-0"
                            >
                              Disconnect
                            </button>
                          </div>

                          <div className="ns-well p-4">
                            <p className="text-sm font-semibold text-ink mb-2">Character limits</p>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="rounded-lg border border-cream-300 bg-white p-3">
                                <p className="ns-t-label mb-1">Basic</p>
                                <p className="font-semibold text-ink">280 characters</p>
                              </div>
                              <div className="rounded-lg border border-cream-300 bg-white p-3">
                                <p className="ns-t-label mb-1">Premium</p>
                                <p className="font-semibold text-ink">25,000 characters</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="ns-empty">
                          <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">
                            <Twitter size={32} />
                          </span>
                          <h3 className="ns-empty-title">Connect your account</h3>
                          <p className="ns-empty-text">
                            Link your Twitter/X account to share generated images and videos directly from the canvas.
                          </p>
                          <button
                            onClick={onShowTwitterAuth}
                            className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat mt-3"
                          >
                            <Twitter size={14} />
                            Connect Twitter
                          </button>
                        </div>
                      )}
                    </section>

                    <section className="ns-card">
                      <h3 className="text-lg font-semibold text-ink mb-3">How to share</h3>
                      <ol className="space-y-3">
                        <li className="flex items-start gap-3">
                          <span className="ns-mark ns-mark-lavender [--size:24px] text-xs font-semibold" aria-hidden="true">1</span>
                          <span className="text-sm text-ink-soft">Add a <strong>Post node</strong> to your canvas from the toolbar</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="ns-mark ns-mark-lavender [--size:24px] text-xs font-semibold" aria-hidden="true">2</span>
                          <span className="text-sm text-ink-soft">Connect an image or video node, or upload directly</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="ns-mark ns-mark-lavender [--size:24px] text-xs font-semibold" aria-hidden="true">3</span>
                          <span className="text-sm text-ink-soft">Write your caption and post!</span>
                        </li>
                      </ol>
                    </section>
                  </div>
                )}

                {/* Auto-Save Tab */}
                {activeTab === 'autosave' && (
                  <div className="space-y-4">
                    <section className="ns-card">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="ns-mark ns-mark-lavender" aria-hidden="true">
                          <HardDrive size={20} />
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-lg font-semibold text-ink">Local auto-save</h2>
                          <p className="text-sm text-ink-soft">Automatically save generated files to a local folder</p>
                        </div>
                      </div>

                      {!autoSaveSupported ? (
                        <div className="ns-note p-4 text-sm" data-tone="warn">
                          <p>
                            <strong>Not supported:</strong> Your browser doesn't support the File System Access API.
                            Try using Chrome or Edge browser.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            {/* Choosing the folder is the action here until there is one; after that, changing it is the lesser one */}
                            <button
                              onClick={onSelectLocalFolder}
                              className={`ns-btn ns-btn-sm ns-btn-flat ${autoSaveFolderName ? 'ns-btn-ghost' : 'ns-btn-primary'}`}
                            >
                              <FolderOpen size={14} />
                              {autoSaveFolderName ? 'Change folder' : 'Select folder'}
                            </button>

                            {autoSaveFolderName && (
                              <span className="text-sm text-ink-soft flex items-center gap-2 min-w-0">
                                <Check size={16} className="text-ok shrink-0" />
                                <span className="truncate">{autoSaveFolderName}</span>
                              </span>
                            )}
                          </div>

                          {/* Each account's files go under a folder named after it, so accounts that share a folder stay apart */}
                          {autoSaveFolderName && autoSaveAccountFolder && (
                            <p className="ns-t-meta" data-account-folder>
                              Your files go in a folder of your own inside it, <span className="font-semibold text-ink-soft">{autoSaveAccountFolder}</span>, so another account using the same folder keeps its files apart from yours.
                            </p>
                          )}

                          {autoSaveFolderName && (
                            <label className="ns-well flex items-center justify-between gap-3 p-3 cursor-pointer">
                              <span className="text-sm font-medium text-ink">
                                Auto-save images and videos to local folder
                              </span>
                              <input
                                type="checkbox"
                                role="switch"
                                checked={autoSaveEnabled}
                                onChange={(e) => onAutoSaveEnabledChange(e.target.checked)}
                                className="ns-switch"
                              />
                            </label>
                          )}
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {/* ComfyUI Tab */}
                {activeTab === 'comfyui' && (
                  <div className="space-y-4">
                    <section className="ns-card">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="ns-mark ns-mark-lavender" aria-hidden="true">
                          <Server size={20} />
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-lg font-semibold text-ink">ComfyUI local models</h2>
                          <p className="text-sm text-ink-soft">Connect to your local ComfyUI server for image generation</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label htmlFor="comfyui-server-url" className="ns-field-label">ComfyUI server URL</label>
                          <div className="flex gap-2">
                            <input
                              id="comfyui-server-url"
                              type="text"
                              className="ns-field flex-1 min-w-0"
                              value={localComfyConfig.baseUrl}
                              onChange={(e) => setLocalComfyConfig({...localComfyConfig, baseUrl: e.target.value})}
                              placeholder="http://127.0.0.1:8188"
                            />
                            <button
                              onClick={handleTestComfyUI}
                              disabled={comfyUIStatus === 'testing'}
                              className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat shrink-0"
                            >
                              {comfyUIStatus === 'testing' ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <RefreshCw size={14} />
                              )}
                              Test
                            </button>
                          </div>

                          {comfyUIStatus === 'connected' && (
                            <p className="ns-t-small mt-1.5 flex items-center gap-1 text-ok-deep">
                              <Check size={12} /> Connected to ComfyUI
                            </p>
                          )}
                          {comfyUIStatus === 'error' && (
                            <p className="ns-t-small mt-1.5 text-danger-deep">
                              {comfyUIError || 'Connection failed'}
                            </p>
                          )}
                        </div>

                        <label className="ns-well flex items-center justify-between gap-3 p-3 cursor-pointer">
                          <span className="text-sm font-medium text-ink">
                            Enable ComfyUI for local generation
                          </span>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={localComfyConfig.enabled}
                            onChange={(e) => setLocalComfyConfig({...localComfyConfig, enabled: e.target.checked})}
                            className="ns-switch"
                          />
                        </label>

                        <div className="flex justify-end">
                          <button onClick={handleSaveComfyConfig} className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat">
                            Save settings
                          </button>
                        </div>
                      </div>
                    </section>

                    {/* Custom Workflows Section */}
                    <section className="ns-card">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <h3 className="text-lg font-semibold text-ink">Custom workflows</h3>
                        <button
                          onClick={onRefreshWorkflows}
                          disabled={customWorkflowsLoading}
                          className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat"
                        >
                          <RefreshCw size={14} className={customWorkflowsLoading ? 'animate-spin' : ''} />
                          Refresh
                        </button>
                      </div>

                      {customWorkflowError && (
                        <p className="ns-note mb-3" data-tone="warn">{customWorkflowError}</p>
                      )}

                      {customWorkflows.length > 0 ? (
                        <>
                          {customWorkflowInfo && (
                            <p className="text-sm text-ink-soft mb-3">{customWorkflowInfo}</p>
                          )}
                          <div className="space-y-2">
                            {customWorkflows.map((wf) => (
                              <div key={wf.filename} className="ns-well flex items-center justify-between gap-3 p-3">
                                <span className="text-sm font-medium text-ink truncate">{wf.name}</span>
                                <span className="ns-t-meta shrink-0">{wf.filename}</span>
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={onEnsureWorkflowsFolder}
                            className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat mt-4"
                          >
                            <FolderOpen size={14} /> Create workflows folder
                          </button>
                        </>
                      ) : (
                        /* Nothing found: what the last look at the folder said, or how to get workflows in */
                        <div className="ns-empty">
                          <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">
                            <FolderOpen size={32} />
                          </span>
                          <p className="ns-empty-title">No custom workflows</p>
                          <p className="ns-empty-text">
                            {customWorkflowInfo || 'Enable auto-save and add JSON files to the workflows folder.'}
                          </p>
                          <button
                            onClick={onEnsureWorkflowsFolder}
                            className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat mt-3"
                          >
                            <FolderOpen size={14} /> Create workflows folder
                          </button>
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {/* Account Tab */}
                {activeTab === 'claude' && user && <ClaudeConnectorPanel />}

                {activeTab === 'admin' && user && isAdmin && (
                  <AdminPanel admin={{ id: user.sub, email: user.email }} />
                )}

                {user && (activeTab === 'account' || activeTab === 'devices') && (
                  <div className="space-y-4">
                    {activeTab === 'account' && (
                    <section className="ns-card">
                      <h2 className="text-lg font-semibold text-ink mb-4">Profile</h2>
                      <div className="flex items-center gap-4 pb-4 mb-2 border-b border-cream-200">
                        <span className={`ns-avatar [--size:56px]${isPro ? ' ring-2 ring-amber-500 ring-offset-2 ring-offset-white' : ''}`} aria-hidden="true">
                          {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : user.name.charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="text-base font-bold text-ink truncate flex items-center gap-2">{user.name}{isPro && <span className="ns-badge ns-badge-ok">Pro</span>}</p>
                          <p className="text-sm text-ink-soft truncate">{user.email}</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-4 py-2 border-b border-cream-200">
                          <span className="text-sm text-ink-soft">Email</span>
                          <span className="text-sm font-medium text-ink truncate">{user.email}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 py-2 border-b border-cream-200">
                          <span className="text-sm text-ink-soft">Name</span>
                          <span className="text-sm font-medium text-ink truncate">{user.name}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 py-2">
                          <span className="text-sm text-ink-soft">User ID <span className="ns-t-meta">· for support requests</span></span>
                          <span className="ns-t-mono text-ink-soft">{user.sub.slice(0, 16)}...</span>
                        </div>
                      </div>
                    </section>
                    )}

                    {/* Devices & Sessions Section */}
                    {activeTab === 'devices' && (
                    <section className="ns-card">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div>
                          <h2 className="text-lg font-semibold text-ink">Devices and sessions</h2>
                          <p className="text-sm text-ink-soft mt-0.5">
                            {activeSessions ? `${activeSessions.length} of ${SESSION_LIMIT} devices` : 'Loading sessions...'}
                          </p>
                        </div>
                        {activeSessions && activeSessions.length > 1 && onRevokeAllOtherSessions && (
                          <button
                            onClick={async () => {
                              setRevokingAll(true);
                              await onRevokeAllOtherSessions();
                              setRevokingAll(false);
                            }}
                            disabled={revokingAll}
                            className="ns-btn ns-btn-sm ns-btn-danger ns-btn-flat shrink-0"
                          >
                            {revokingAll ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                            Sign out all others
                          </button>
                        )}
                      </div>

                      {activeSessions && activeSessions.length > 0 ? (
                        <div className="space-y-2">
                          {activeSessions.map((session) => {
                            const isCurrent = session.sessionId === currentSessionId;
                            const isRevoking = revokingSessionId === session.sessionId;
                            const lastActive = session.lastActiveAt;
                            const diffMin = Math.floor((Date.now() - lastActive) / 60000);
                            const activeNow = isCurrent || diffMin < 2;
                            // How long ago, in the words the library uses for it
                            const timeAgo = activeNow ? 'Active now' : formatRelativeTime(lastActive);

                            const DeviceIcon = session.deviceType === 'mobile' ? Smartphone
                              : session.deviceType === 'tablet' ? Tablet
                              : Monitor;

                            return (
                              <div
                                key={session.sessionId}
                                className={`ns-well flex items-center justify-between gap-3 p-3 ${isCurrent ? 'border-lavender' : ''}`}
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  {/* This device in lavender, the others quiet */}
                                  <span
                                    className={`ns-mark ns-mark-sm ${isCurrent ? 'ns-mark-lavender' : ''}`}
                                    data-tone={isCurrent ? undefined : 'ink'}
                                    aria-hidden="true"
                                  >
                                    <DeviceIcon size={16} />
                                  </span>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-ink truncate">
                                        {session.deviceName}
                                      </p>
                                      {isCurrent && (
                                        <span className="ns-chip ns-chip-sm ns-chip-lavender whitespace-nowrap">
                                          This device
                                        </span>
                                      )}
                                    </div>
                                    <p className="ns-t-small flex items-center gap-1.5 mt-0.5">
                                      <span
                                        className="ns-tone-dot"
                                        data-tone={activeNow ? 'ok' : diffMin < 60 ? 'warn' : 'ink'}
                                      />
                                      {timeAgo}
                                      <span className="text-ink-faint" aria-hidden="true">·</span>
                                      {session.os}
                                    </p>
                                  </div>
                                </div>

                                {!isCurrent && onRevokeSession && (
                                  <button
                                    onClick={async () => {
                                      setRevokingSessionId(session.sessionId);
                                      await onRevokeSession(session.sessionId);
                                      setRevokingSessionId(null);
                                    }}
                                    disabled={isRevoking}
                                    className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-danger"
                                    title="Sign out this device"
                                    aria-label="Sign out this device"
                                  >
                                    {isRevoking ? <Loader2 className="animate-spin" /> : <X />}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : activeSessions ? (
                        <p className="text-sm text-ink-faint text-center py-4">No active sessions found.</p>
                      ) : (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 size={16} className="animate-spin text-ink-faint" />
                          <span className="text-sm text-ink-faint ml-2">Loading sessions...</span>
                        </div>
                      )}

                      <div className="mt-4 pt-3 border-t border-cream-200">
                        <p className="ns-t-small">
                          Sessions inactive for more than 24 hours are automatically removed.
                          Maximum {SESSION_LIMIT} concurrent devices per account.
                        </p>
                      </div>
                    </section>
                    )}

                    {/* App Version */}
                    {activeTab === 'devices' && (
                    <section className="ns-card">
                      <h2 className="text-lg font-semibold text-ink mb-3 flex items-center gap-2">
                        <Shield size={16} className="text-ink-soft" /> App version
                      </h2>
                      <div className="grid grid-cols-2 gap-y-2 text-sm">
                        {desktopVersion && (
                          <>
                            <span className="text-ink-soft">Desktop app</span>
                            <span className="ns-t-mono text-ink" data-desktop-version>{desktopVersion}</span>
                          </>
                        )}
                        <span className="text-ink-soft">Version</span>
                        <span className="ns-t-mono text-ink">{appVersion.version}</span>
                        <span className="text-ink-soft">Build</span>
                        <span className="ns-t-mono text-ink">{appVersion.gitCommit}</span>
                        <span className="text-ink-soft">Built</span>
                        <span className="font-medium text-ink">
                          {appVersion.buildTime
                            ? new Date(appVersion.buildTime).toLocaleDateString(undefined, {
                                year: 'numeric', month: 'short', day: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })
                            : '—'}
                        </span>
                      </div>
                    </section>
                    )}

                    {activeTab === 'account' && (
                    <section className="ns-card border-[color:var(--tone-line)]" data-tone="danger">
                      <h2 className="text-lg font-semibold text-danger-deep mb-2">Danger zone</h2>
                      <p className="text-sm text-ink-soft mb-4">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>

                      {!showDeleteConfirm ? (
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="ns-btn ns-btn-sm ns-btn-danger ns-btn-flat"
                        >
                          <Trash2 size={14} />
                          Delete account
                        </button>
                      ) : (
                        <div className="ns-well p-4 space-y-3">
                          <p className="text-sm font-medium text-danger-deep">
                            Type "DELETE" to confirm account deletion:
                          </p>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="ns-field"
                            placeholder="Type DELETE"
                            aria-label="Type DELETE to confirm"
                          />
                          {/* As in every dialog: Cancel first, the action last, at the right */}
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setShowDeleteConfirm(false);
                                setDeleteConfirmText('');
                              }}
                              className="ns-btn ns-btn-sm ns-btn-soft ns-btn-flat"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                if (deleteConfirmText === 'DELETE' && onDeleteAccount) {
                                  onDeleteAccount();
                                }
                              }}
                              disabled={deleteConfirmText !== 'DELETE'}
                              className="ns-btn ns-btn-sm ns-btn-danger ns-btn-flat"
                            >
                              Confirm delete
                            </button>
                          </div>
                        </div>
                      )}
                    </section>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Footer: the last line every page has, in the measure of this page */}
        <footer className="py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm">
          <div className="w-full flex items-center justify-between gap-3">
            <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
            {/* The links go through the app's router; drawn without one (a test, a harness) the line stands alone */}
            {inRouter && (
              <nav aria-label="Footer" className="flex items-center gap-1">
                <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
                <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
                <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
              </nav>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SettingsPage;
